import { previewText, relTime, type DealCommsSummary } from "../../lib/commsStrip";

/**
 * One line on a board card: the newest text on the deal (direction arrow,
 * relative time, the first ~80 characters), a red "Needs reply" badge when the
 * homeowner spoke last, or "No texts yet". Tapping it opens the reply drawer.
 * (Finance → Supplements, Oct 16, 2026.)
 */
export default function CommsStrip({
  summary,
  onOpen,
  className = "",
}: {
  /** null = no text on the deal in the window */
  summary: DealCommsSummary | null;
  onOpen: () => void;
  className?: string;
}) {
  const inbound = summary?.direction === "inbound";
  const needsReply = !!summary?.needsReply;

  function open(e: React.SyntheticEvent) {
    e.stopPropagation();
    onOpen();
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open(e);
        }
      }}
      title={
        summary
          ? "Open the text thread — read the homeowner's texts and reply without leaving the board"
          : "No texts on this deal yet — open the thread to send the first one"
      }
      className={
        "group flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] leading-snug cursor-pointer transition " +
        (needsReply
          ? "border-red-200 bg-red-50/80 hover:bg-red-50"
          : "border-slate-200 bg-white/70 hover:bg-white hover:border-slate-300") +
        " " +
        className
      }
    >
      {summary ? (
        <>
          <span
            className={"flex-shrink-0 font-bold " + (inbound ? "text-blue-600" : "text-emerald-600")}
            aria-label={inbound ? "From the homeowner" : "Sent to the homeowner"}
            title={inbound ? "From the homeowner" : "Sent to the homeowner"}
          >
            {inbound ? "↙" : "↗"}
          </span>
          <span className="flex-shrink-0 text-slate-400 tabular-nums">{relTime(summary.at)}</span>
          <span className={"min-w-0 flex-1 truncate " + (inbound ? "text-slate-800" : "text-slate-600")}>
            {previewText(summary.body, 80) || <span className="italic text-slate-400">(no text — media only)</span>}
          </span>
          {needsReply && (
            <span className="flex-shrink-0 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              Needs reply
            </span>
          )}
        </>
      ) : (
        <>
          <span className="flex-shrink-0 text-slate-400">💬</span>
          <span className="min-w-0 flex-1 italic text-slate-400">No texts yet</span>
        </>
      )}
      <span className="flex-shrink-0 text-slate-300 group-hover:text-bardin-teal" aria-hidden="true">›</span>
    </div>
  );
}
